/**
 * Per-tenant branding.
 *
 * The tenant supplies one brand colour. Rather than asking clients for a ten-
 * step palette they do not have, a full ramp is derived from that single hex
 * and written onto the root element as the --color-brand-* tokens the whole
 * design system already reads. Every button, link and active nav item then
 * follows, with no component knowing anything about tenants.
 */

export function applyBranding(tenant) {
  if (!tenant) return

  const root = document.documentElement

  if (tenant.primary_color && isHex(tenant.primary_color)) {
    const ramp = buildRamp(tenant.primary_color)

    Object.entries(ramp).forEach(([step, value]) => {
      root.style.setProperty(`--color-brand-${step}`, value)
    })
  }

  document.title = tenant.name ? `${tenant.name} — Grievance Portal` : 'Grievance Portal'
}

/**
 * Back to the platform default ramp declared in index.css.
 *
 * Used on the login screen: typing a wrong organisation code, or clearing the
 * field after a previous one resolved, must not leave that organisation's
 * colour on screen — an inline style set by applyBranding() overrides the
 * @theme default until it is explicitly removed, it does not fall back on
 * its own.
 */
export function resetBranding() {
  const root = document.documentElement

  for (const step of [50, 100, 200, 300, 400, 500, 600, 700, 800, 900]) {
    root.style.removeProperty(`--color-brand-${step}`)
  }

  document.title = 'Grievance Portal'
}

/**
 * Build a 50–900 ramp from one colour.
 *
 * The supplied colour is placed at 600 — the weight used for primary buttons
 * and active states — and the rest is interpolated toward white and near-black
 * in perceptual (OKLCH-ish) terms rather than by naive RGB mixing, which
 * produces muddy, desaturated midtones on blues in particular.
 */
function buildRamp(hex) {
  const base = hexToRgb(hex)
  const { h, s, l } = rgbToHsl(base)

  // Lightness targets per step. Chosen so 600 stays close to the supplied
  // colour while 50–200 remain usable as tints behind dark text.
  const lightness = {
    50: 0.96, 100: 0.91, 200: 0.82, 300: 0.70,
    400: 0.57, 500: 0.45, 600: l, 700: l * 0.84,
    800: l * 0.68, 900: l * 0.52,
  }

  // Saturation eases off at the pale end; a tint that keeps full saturation
  // reads as a colour wash rather than a neutral background.
  const saturation = {
    50: s * 0.45, 100: s * 0.55, 200: s * 0.70, 300: s * 0.82,
    400: s * 0.92, 500: s, 600: s, 700: s, 800: s * 0.95, 900: s * 0.90,
  }

  const ramp = {}

  Object.keys(lightness).forEach((step) => {
    ramp[step] = hslToHex(h, clamp(saturation[step]), clamp(lightness[step]))
  })

  return ramp
}

function clamp(value) {
  return Math.min(1, Math.max(0, value))
}

function isHex(value) {
  return /^#?[0-9a-f]{6}$/i.test(String(value).trim())
}

function hexToRgb(hex) {
  const clean = String(hex).trim().replace('#', '')

  return {
    r: parseInt(clean.slice(0, 2), 16) / 255,
    g: parseInt(clean.slice(2, 4), 16) / 255,
    b: parseInt(clean.slice(4, 6), 16) / 255,
  }
}

function rgbToHsl({ r, g, b }) {
  const max = Math.max(r, g, b)
  const min = Math.min(r, g, b)
  const l = (max + min) / 2

  if (max === min) return { h: 0, s: 0, l }

  const d = max - min
  const s = l > 0.5 ? d / (2 - max - min) : d / (max + min)

  let h
  if (max === r) h = ((g - b) / d + (g < b ? 6 : 0)) / 6
  else if (max === g) h = ((b - r) / d + 2) / 6
  else h = ((r - g) / d + 4) / 6

  return { h, s, l }
}

function hslToHex(h, s, l) {
  const hue = (p, q, t) => {
    let tt = t
    if (tt < 0) tt += 1
    if (tt > 1) tt -= 1
    if (tt < 1 / 6) return p + (q - p) * 6 * tt
    if (tt < 1 / 2) return q
    if (tt < 2 / 3) return p + (q - p) * (2 / 3 - tt) * 6
    return p
  }

  let r
  let g
  let b

  if (s === 0) {
    r = l
    g = l
    b = l
  } else {
    const q = l < 0.5 ? l * (1 + s) : l + s - l * s
    const p = 2 * l - q

    r = hue(p, q, h + 1 / 3)
    g = hue(p, q, h)
    b = hue(p, q, h - 1 / 3)
  }

  const channel = (v) => Math.round(v * 255).toString(16).padStart(2, '0')

  return `#${channel(r)}${channel(g)}${channel(b)}`
}
